import { useState } from 'react'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Settings2, Plus, Pencil, FileText, Building2,
  Search, ShieldCheck,
} from 'lucide-react'
import {
  useTiposIdentificacionFull,
  useTiposLaboratorioFull,
  useCrearTipoIdentificacion,
  useActualizarTipoIdentificacion,
  useCrearTipoLaboratorioItem,
  useActualizarTipoLaboratorioItem,
} from '../hooks/useBdClientes'
import type {
  TipoIdentificacionFull,
  TipoLaboratorioFull,
} from '../types'

// ============================================================================
// Modal Tipo Identificación
// ============================================================================

function TipoIdentificacionModal({
  open,
  onOpenChange,
  editItem,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  editItem: TipoIdentificacionFull | null
}) {
  const [form, setForm] = useState({
    nombre: editItem?.nombre || '',
    codigo: editItem?.codigo || '',
    longitud_esperada: editItem?.longitud_esperada?.toString() || '',
    formato_regex: editItem?.formato_regex || '',
    descripcion: editItem?.descripcion || '',
    activo: editItem?.activo ?? true,
  })

  const crearMutation = useCrearTipoIdentificacion()
  const actualizarMutation = useActualizarTipoIdentificacion()
  const isEditing = !!editItem
  const isPending = crearMutation.isPending || actualizarMutation.isPending

  const handleSubmit = async () => {
    if (!form.nombre.trim() || !form.codigo.trim()) return
    const payload = {
      nombre: form.nombre.trim(),
      codigo: form.codigo.trim(),
      longitud_esperada: form.longitud_esperada ? Number(form.longitud_esperada) : null,
      formato_regex: form.formato_regex.trim() || null,
      descripcion: form.descripcion.trim() || null,
      activo: form.activo,
    }
    try {
      if (isEditing && editItem) {
        await actualizarMutation.mutateAsync({ id: editItem.id, payload })
      } else {
        await crearMutation.mutateAsync(payload)
      }
      onOpenChange(false)
    } catch { /* handled by hook */ }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Editar Tipo de Identificación' : 'Nuevo Tipo de Identificación'}</DialogTitle>
          <DialogDescription>
            {isEditing ? 'Modifica los datos del tipo de identificación' : 'Ingresa los datos del nuevo tipo'}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>Nombre *</Label>
              <Input
                value={form.nombre}
                onChange={(e) => setForm(prev => ({ ...prev, nombre: e.target.value }))}
                placeholder="Ej: RUC"
              />
            </div>
            <div className="grid gap-2">
              <Label>Código *</Label>
              <Input
                value={form.codigo}
                onChange={(e) => setForm(prev => ({ ...prev, codigo: e.target.value }))}
                placeholder="Ej: RUC"
                maxLength={10}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>Longitud esperada</Label>
              <Input
                type="number"
                value={form.longitud_esperada}
                onChange={(e) => setForm(prev => ({ ...prev, longitud_esperada: e.target.value }))}
                placeholder="Ej: 13"
              />
            </div>
            <div className="grid gap-2">
              <Label>Formato (Regex)</Label>
              <Input
                value={form.formato_regex}
                onChange={(e) => setForm(prev => ({ ...prev, formato_regex: e.target.value }))}
                placeholder="Expresión regular"
              />
            </div>
          </div>
          <div className="grid gap-2">
            <Label>Descripción</Label>
            <Textarea
              value={form.descripcion}
              onChange={(e) => setForm(prev => ({ ...prev, descripcion: e.target.value }))}
              placeholder="Descripción opcional..."
              rows={2}
            />
          </div>
          <div className="flex items-center justify-between">
            <Label>Activo</Label>
            <Switch
              checked={form.activo}
              onCheckedChange={(checked) => setForm(prev => ({ ...prev, activo: checked }))}
              className="data-[state=checked]:bg-teal-500"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            disabled={!form.nombre.trim() || !form.codigo.trim() || isPending}
            className="bg-teal-800 hover:bg-teal-900 text-white"
          >
            {isPending ? 'Guardando...' : isEditing ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ============================================================================
// Modal Tipo Laboratorio
// ============================================================================

function TipoLaboratorioModal({
  open,
  onOpenChange,
  editItem,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  editItem: TipoLaboratorioFull | null
}) {
  const [form, setForm] = useState({
    nombre: editItem?.nombre || '',
    descripcion: editItem?.descripcion || '',
    requiere_documentacion_extra: editItem?.requiere_documentacion_extra ?? false,
    activo: editItem?.activo ?? true,
  })

  const crearMutation = useCrearTipoLaboratorioItem()
  const actualizarMutation = useActualizarTipoLaboratorioItem()
  const isEditing = !!editItem
  const isPending = crearMutation.isPending || actualizarMutation.isPending

  const handleSubmit = async () => {
    if (!form.nombre.trim()) return
    const payload = {
      nombre: form.nombre.trim(),
      descripcion: form.descripcion.trim() || null,
      requiere_documentacion_extra: form.requiere_documentacion_extra,
      activo: form.activo,
    }
    try {
      if (isEditing && editItem) {
        await actualizarMutation.mutateAsync({ id: editItem.id, payload })
      } else {
        await crearMutation.mutateAsync(payload)
      }
      onOpenChange(false)
    } catch { /* handled by hook */ }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Editar Tipo de Laboratorio' : 'Nuevo Tipo de Laboratorio'}</DialogTitle>
          <DialogDescription>
            {isEditing ? 'Modifica los datos del tipo' : 'Ingresa los datos del nuevo tipo'}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label>Nombre *</Label>
            <Input
              value={form.nombre}
              onChange={(e) => setForm(prev => ({ ...prev, nombre: e.target.value }))}
              placeholder="Ej: Nacional"
            />
          </div>
          <div className="grid gap-2">
            <Label>Descripción</Label>
            <Textarea
              value={form.descripcion}
              onChange={(e) => setForm(prev => ({ ...prev, descripcion: e.target.value }))}
              placeholder="Descripción opcional..."
              rows={3}
            />
          </div>
          <div className="flex items-center justify-between">
            <Label>Requiere documentación extra</Label>
            <Switch
              checked={form.requiere_documentacion_extra}
              onCheckedChange={(checked) => setForm(prev => ({ ...prev, requiere_documentacion_extra: checked }))}
              className="data-[state=checked]:bg-teal-500"
            />
          </div>
          <div className="flex items-center justify-between">
            <Label>Activo</Label>
            <Switch
              checked={form.activo}
              onCheckedChange={(checked) => setForm(prev => ({ ...prev, activo: checked }))}
              className="data-[state=checked]:bg-teal-500"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            disabled={!form.nombre.trim() || isPending}
            className="bg-teal-800 hover:bg-teal-900 text-white"
          >
            {isPending ? 'Guardando...' : isEditing ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ============================================================================
// Sección genérica
// ============================================================================

function ConfigSection<T extends { id: number; nombre: string; activo: boolean }>({
  title,
  icon: Icon,
  items,
  isLoading,
  onAdd,
  onEdit,
  renderSecondary,
}: {
  title: string
  icon: React.ElementType
  items: T[] | undefined
  isLoading: boolean
  onAdd: () => void
  onEdit: (item: T) => void
  renderSecondary?: (item: T) => string | null
}) {
  const [search, setSearch] = useState('')

  const filtered = items?.filter(item =>
    item.nombre.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <Card className="border border-gray-300 dark:border-gray-700">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Icon className="h-4 w-4 text-teal-500" />
            {title}
            {items && (
              <Badge className="badge-gradient hover:opacity-90 ml-1">{items.length}</Badge>
            )}
          </CardTitle>
          <Button
            size="sm"
            onClick={onAdd}
            className="bg-teal-800 hover:bg-teal-900 text-white"
          >
            <Plus className="mr-1 h-3.5 w-3.5" />
            Crear
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={`Buscar ${title.toLowerCase()}...`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-12" />
            ))}
          </div>
        ) : (
          <div className="max-h-[350px] overflow-y-auto space-y-1">
            {filtered && filtered.length > 0 ? (
              filtered.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm truncate">{item.nombre}</span>
                      {item.activo ? (
                        <Badge className="badge-gradient hover:opacity-90 text-xs">Activo</Badge>
                      ) : (
                        <Badge variant="outline" className="text-gray-400 border-gray-400 text-xs">Inactivo</Badge>
                      )}
                    </div>
                    {renderSecondary && (
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">
                        {renderSecondary(item) || ''}
                      </p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(item)}
                    className="ml-2 text-muted-foreground hover:text-teal-600"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Icon className="h-8 w-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">
                  {search ? 'No se encontraron resultados' : `No hay ${title.toLowerCase()} registrados`}
                </p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ============================================================================
// Página principal
// ============================================================================

export function LaboratoriosConfigPage() {
  useDocumentTitle('Laboratorios - Configuración')

  const { data: tiposIdentificacion, isLoading: tiposIdLoading } = useTiposIdentificacionFull()
  const { data: tiposLaboratorio, isLoading: tiposLabLoading } = useTiposLaboratorioFull()

  const [tipoIdModalOpen, setTipoIdModalOpen] = useState(false)
  const [tipoLabModalOpen, setTipoLabModalOpen] = useState(false)
  const [editTipoId, setEditTipoId] = useState<TipoIdentificacionFull | null>(null)
  const [editTipoLab, setEditTipoLab] = useState<TipoLaboratorioFull | null>(null)

  const handleOpenTipoIdModal = (item?: TipoIdentificacionFull) => {
    setEditTipoId(item || null)
    setTipoIdModalOpen(true)
  }

  const handleOpenTipoLabModal = (item?: TipoLaboratorioFull) => {
    setEditTipoLab(item || null)
    setTipoLabModalOpen(true)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <Settings2 className="h-6 w-6 text-teal-500" />
          Configuración de Laboratorios
        </h1>
      </div>

      {/* Info banner */}
      <div className="flex items-center gap-2 p-3 rounded-lg bg-teal-500/5 border border-teal-500/20 text-sm text-muted-foreground">
        <ShieldCheck className="h-4 w-4 text-teal-500 flex-shrink-0" />
        Los registros con laboratorios asociados no se pueden eliminar. Utiliza el estado "Inactivo" para ocultarlos de los formularios.
      </div>

      {/* Config sections */}
      <div className="grid gap-6 lg:grid-cols-2">
        <ConfigSection
          title="Tipos de Identificación"
          icon={FileText}
          items={tiposIdentificacion}
          isLoading={tiposIdLoading}
          onAdd={() => handleOpenTipoIdModal()}
          onEdit={(item) => handleOpenTipoIdModal(item as TipoIdentificacionFull)}
          renderSecondary={(item) => {
            const ti = item as TipoIdentificacionFull
            return ti.codigo ? `Código: ${ti.codigo}${ti.longitud_esperada ? ` | Longitud: ${ti.longitud_esperada}` : ''}` : null
          }}
        />

        <ConfigSection
          title="Tipos de Laboratorio"
          icon={Building2}
          items={tiposLaboratorio}
          isLoading={tiposLabLoading}
          onAdd={() => handleOpenTipoLabModal()}
          onEdit={(item) => handleOpenTipoLabModal(item as TipoLaboratorioFull)}
          renderSecondary={(item) => {
            const tl = item as TipoLaboratorioFull
            return tl.requiere_documentacion_extra ? 'Requiere doc. extra' : tl.descripcion
          }}
        />
      </div>

      {/* Modals */}
      {tipoIdModalOpen && (
        <TipoIdentificacionModal
          open={tipoIdModalOpen}
          onOpenChange={setTipoIdModalOpen}
          editItem={editTipoId}
        />
      )}

      {tipoLabModalOpen && (
        <TipoLaboratorioModal
          open={tipoLabModalOpen}
          onOpenChange={setTipoLabModalOpen}
          editItem={editTipoLab}
        />
      )}
    </div>
  )
}
